/**
 * Shared Date Utilities
 */

/**
 * Converts a date (string or object) into a "Ad-Hoc Pacific Date".
 * This returns a Date object where the UTC components match the Pacific components of the input.
 * Example: Input "2026-02-01T20:00:00Z" (Feb 1 12PM PST) -> Output Date(2026-02-01T00:00:00Z)
 * 
 * This allows us to use .getUTCDate(), .getUTCMonth(), etc. and get the Pacific values.
 */
export function toAdHocPacificDate(date: Date | string): Date {
    // If it's a simple date string (YYYY-MM-DD), assume it is ALREADY the local date we want.
    // treat it as UTC Midnight so .getUTCDate() returns the correct day.
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return new Date(date);
    }

    const d = new Date(date);
    // Get the parts in Pacific Time
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false
    }).formatToParts(d);

    const partMap: Record<string, number> = {};
    parts.forEach(p => {
        if (p.type !== 'literal') partMap[p.type] = parseInt(p.value, 10);
    });

    // Create a new Date using these parts as UTC
    return new Date(Date.UTC(
        partMap['year'],
        partMap['month'] - 1,
        partMap['day'],
        partMap['hour'] || 0,
        partMap['minute'] || 0,
        partMap['second'] || 0
    ));
}

/**
 * Generates grouping key YYYY-MM based on Pacific Time.
 */
export function getPacificMonthKey(date: Date | string): string {
    const d = toAdHocPacificDate(date);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * Parses an event date string into a Local Date object for frontend display.
 * - YYYY-MM-DD: Parsed as Local Midnight (preserves date regardless of user TZ).
 * - ISO: Parsed as standard Date (time-shifted to user's local TZ).
 */
export function parseEventDate(dateString: string): Date {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [y, m, d] = dateString.split('-').map(Number);
        return new Date(y, m - 1, d);
    }
    return new Date(dateString);
}

/**
 * Returns a YYYY-MM-DD string based on Pacific Time.
 */
export function getPacificDateString(date: Date | string): string {
    const d = toAdHocPacificDate(date);
    return d.toISOString().split('T')[0];
}

/**
 * Parse a YYYY-MM-DD input (from a date picker) into a Date stamped at
 * 20:00 UTC — which is noon Pacific during PST and 1pm Pacific during PDT.
 *
 * Why: users enter race dates in a timezone-less date picker. `new Date('YYYY-MM-DD')`
 * parses as UTC midnight, which in Pacific Time is the previous day — so a race
 * created for April 26 would show as April 25 in client browsers west of UTC.
 * Stamping as ~noon Pacific makes the date render consistently across North American
 * time zones and still represents "that calendar date" unambiguously.
 *
 * Pass-through for non-date-only strings and Date objects.
 */
export function pacificNoonFromDateString(input: string): Date {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(input)) {
        return new Date(input);
    }
    const [y, m, d] = input.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d, 20, 0, 0));
}
